'use client';

import './home-mobile.scss';
import React, { useContext, useEffect, useRef } from 'react'
import ShowreelCanvas from './Showreel/ShowreelCanvas';
import MobileShellContext from '@/components/Mobile/MobileShellContext';

interface Props {
  handleNextStep: () => void;
  isActive: boolean;
}

export default function Title(props: Props) {
  const { handleNextStep, isActive } = props;
  const { setShowreelVideo } = useContext(MobileShellContext);

  const canvasElementRef = useRef<HTMLCanvasElement>(null)
  const canvasRef = useRef<ShowreelCanvas | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null)
  const isActiveRef = useRef(isActive);

  useEffect(() => {
    const videoElement = videoRef.current

    function setupRenderCanvas() {
      if (!canvasRef.current && canvasElementRef.current && videoElement) {
        canvasRef.current = new ShowreelCanvas(canvasElementRef.current, videoElement);
        if (isActiveRef.current) canvasRef.current.start();
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
    };

    const handleVideoError = () => {
      // Setup canvas anyway with fallback
      setupRenderCanvas();
    };

    // Setup render canvas upon video loaded and begun playing
    if (videoElement) {      
      // Multiple fallbacks to ensure canvas setup
      videoElement.addEventListener('playing', setupRenderCanvas);
      videoElement.addEventListener('loadeddata', setupRenderCanvas);
      
      videoElement.addEventListener('error', handleVideoError);
      
      // Check if video is already playing (race condition)
      if (videoElement.readyState >= 3) {
        setupRenderCanvas();
      }
      
      // Explicitly attempt to play (autoplay may fail)
      // Autoplay can be refused, or aborted when Swiper's loop reorders this
      // slide mid-play; the muted video resumes via autoplay either way.
      // Setup canvas anyway so it's ready
      videoElement.play().catch(setupRenderCanvas);
    }

    // Disable default behaviour for touchmove event
    window.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      window.removeEventListener('touchmove', handleTouchMove);

      if (videoElement) {
        videoElement.removeEventListener('playing', setupRenderCanvas);
        videoElement.removeEventListener('loadeddata', setupRenderCanvas);
        videoElement.removeEventListener('error', handleVideoError);
      }

      canvasRef.current?.dispose();
      canvasRef.current = null;
    }
  }, []);

  useEffect(() => {
    setShowreelVideo(videoRef.current);
    return () => setShowreelVideo(null);
  }, [setShowreelVideo]);

  useEffect(() => {
    isActiveRef.current = isActive;
    if (isActive) canvasRef.current?.start();
    else canvasRef.current?.stop();
  }, [isActive]);

  return (
    <div className='title-slide'>
      <video className='title-slide-video'
        ref={videoRef}
        autoPlay
        preload='metadata'
        loop
        muted
        playsInline
        src='/assets/video/MobileShowreel_fixed.mp4'
        data-hover='/assets/image/mobile/MobileBG.png'
        aria-label="The Other Lab showreel"
      >
        <track
          kind="captions"
          src="/captions/no-audio.vtt"
          srcLang="en"
          label="No spoken audio"
        />
      </video>
      <canvas ref={canvasElementRef} className='title-slide-canvas' />
    </div>
  )
};
